
/* eslint vue/no-v-html: 0 */
import { mapActions, mapState } from 'vuex';
import promiseErrors from '~/services/promiseErrors';
import IconAsbestos from '~/components/icon/IconAsbestos.vue';
import IconExclamationTriangle from '~/components/icon/IconExclamationTriangle.vue';
import IconGrabBag from '~/components/icon/IconGrabBag.vue';
import IconGrabLorry from '~/components/icon/IconGrabLorry.vue';
import IconMapMarkerAlt from '~/components/icon/IconMapMarkerAlt.vue';
import IconOil from '~/components/icon/IconOil.vue';
import IconPhone from '~/components/icon/IconPhone.vue';
import IconPlasterboard from '~/components/icon/IconPlasterboard.vue';
import IconSkip from '~/components/icon/IconSkip.vue';
import IconSoil from '~/components/icon/IconSoil.vue';
import IconTruck from '~/components/icon/IconTruck.vue';
import IconTyre from '~/components/icon/IconTyre.vue';
import externalLink from '~/mixins/externalLink';
import paginationMixin from '~/mixins/pagination';

const serviceIcons = {
    IconAsbestos,
    IconExclamationTriangle,
    IconGrabBag,
    IconGrabLorry,
    IconOil,
    IconPlasterboard,
    IconSkip,
    IconSoil,
    IconTruck,
    IconTyre,
};

export default {
    name: 'BusinessProfile',
    mixins: [externalLink, paginationMixin],
    async asyncData({ app, error, route }) {
        const [business] = await promiseErrors([app.$axios.$get(`${app.$config.endpoints.organisation}/${route.params.slug}`)], app);
        if (!business) {
            return error({ statusCode: 404, message: app.i18n.t('business.not_found') });
        }
        return {
            business: business ? business.organisation : {},
        };
    },
    data() {
        return {
            business: {},
            distanceUnit: null,
            reportableEndpoint: `${this.$config.endpoints.organisation}/${this.$route.params.slug}/report`,
            reviews: [],
            showConfirmDelete: false,
            showBan: false,
            showReject: false,
            pagination: {},
        };
    },
    async fetch() {
        const [reviews] = await promiseErrors([
            this.$axios.$get(`${this.$config.endpoints.testimonial}`, {
                params: {
                    organisation: this.$route.params.slug,
                    page: this.$route.query.page || 1,
                    per_page: 2,
                },
            }),
        ]);

        this.reviews = reviews ? reviews.testimonials.data : [];
        this.pagination = {
            current: reviews ? reviews.testimonials.current_page : 1,
            next: reviews ? reviews.testimonials.current_page + 1 : 1,
            total: reviews ? reviews.testimonials.last_page : 1,
        };
    },
    head() {
        return {
            title: `${this.business.name} - ClearWaste`,
            meta: [{ hid: 'description', name: 'description', content: this.business.address_1 }],
        };
    },
    computed: {
        ...mapState(['auth']),
        canManage() {
            return this.isAdmin || (this.auth.user && this.auth.user.organisation_id === this.business.id);
        },
        categories() {
            const categories = [];
            this.business.categories.forEach((category) => {
                categories.push({
                    text: category.name,
                    icon: serviceIcons[category.icon],
                });
            });
            return categories;
        },
        isAdmin() {
            return this.auth.user && this.auth.user.role === 'admin';
        },
        licenceUrl() {
            if (!this.business.registration_number) {
                return '#';
            }

            switch (this.business.agency) {
                case 'iepa':
                    return `${this.$config.iepaUrl}${this.business.registration_number}`;
                case 'sepa':
                    return this.$config.sepaUrl;
                case 'niea':
                    return this.$config.nieaUrl;
                case 'nr':
                    return this.$config.nrUrl;
                default:
                    return `${this.$config.environmentAgencyApi}${this.business.registration_number}`;
            }
        },
        items() {
            const details = [];
            if (this.business.public_address) {
                details.push({
                    icon: IconMapMarkerAlt,
                    text: `${this.business.address_1}, ${this.business.address_2}, ${this.business.postcode}`.replace(', ,', ', '),
                });
            }
            if (this.isAdmin) {
                if (this.business.contact_number) {
                    details.push({
                        icon: IconPhone,
                        text: this.business.contact_number,
                    });
                }
                if (this.business.contact_number_2) {
                    details.push({
                        icon: IconPhone,
                        text: this.business.contact_number_2,
                    });
                }
            }
            return details;
        },
        services() {
            const services = [];
            this.business.services.forEach((service) => {
                if (service.name !== 'Specialist') {
                    services.push({
                        text: service.name,
                        icon: serviceIcons[service.icon],
                    });
                }
            });
            return services;
        },
        showAutoQuote() {
            const serviceIds = this.business.services.map((service) => service.id);
            return this.canManage && (serviceIds.includes(2) || serviceIds.includes(3));
        },
        structuredData() {
            return `
            {
                "@context": "https://schema.org/",
                "@type": "LocalBusiness",
                "name": "${this.business.name}",
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "${this.business.average_rating}",
                },
            }`;
        },
    },
    methods: {
        ...mapActions(['showConfirm', 'showReportable']),
        confirmDelete() {
            this.showConfirm({
                callback: this.deleteBusiness,
                text: this.$t('business.delete'),
            });
        },
        async deleteBusiness() {
            const { message } = await promiseErrors([this.$axios.$delete(`${this.$config.endpoints.organisation}/${this.$route.params.slug}`)]);
            this.$store.dispatch('setFlashMessage', { message });
            this.$auth.fetchUser();
            this.$router.push({ name: 'dashboard' });
        },
        async loadReviews() {
            const [reviews] = await promiseErrors([
                this.$axios.$get(`${this.$config.endpoints.testimonial}`, {
                    params: {
                        organisation: this.business.slug,
                        page: this.pagination.next,
                        per_page: 2,
                    },
                }),
            ]);

            this.pagination.next += 1;
            reviews.testimonials.data.forEach((i) => {
                this.reviews.push(i);
            });
        },
        async rejectedOrBan() {
            const organisation = await this.$axios.$get(`${this.$config.endpoints.organisation}/${this.$route.params.slug}`);
            this.showReject = false;
            this.showBan = false;
            this.business = organisation.organisation;
        },
        updateReview({ reviewId, message }) {
            const review = this.reviews.find((checkReview) => checkReview.id === reviewId);
            review.response = message;
        },
        deleteReview(reviewId) {
            this.reviews = this.reviews.filter((review) => review.id !== reviewId);
        },
    },
};
